import { services } from '@cwos/api-client';
import type { schedule as SCH, services as SVC } from '@cwos/api-client';
import { localDateIn } from './coverageLogic.js';

/**
 * Pure derivations for the Operations scheduler canvas — THE ORGANIZATION'S
 * CALENDAR (owner P0: "Schedule = org calendar").
 *
 * Two record kinds share the canvas and are never merged into one another:
 * Phibian SHIFTS (`scheduled_shift`, when a worker is rostered on) and client
 * VISITS (`scheduled_service` — for an EHR tenant, every imported occurrence).
 * Each record becomes exactly one `CalendarItem`; a group visit sits under every
 * credited staff party's row but is still ONE item, so a tally over items counts
 * the occurrence once (the owner's group-service rule).
 *
 * Everything here is arithmetic over rows the server already returned; nothing
 * widens a read or invents a row. Two rules are inherited, not invented:
 *
 *   - a SHIFT's state is coverage (`chipState` — `scheduleLogic.coverageGaps`'
 *     predicate), so the canvas and the coverage list cannot disagree;
 *   - a VISIT's state is the server's canonical `deliveredBucket`
 *     (deliveredServices.ts) — never re-derived from `occurrenceState` or the
 *     clock — so the calendar's delivered count IS the delivered metric,
 *     SOURCE_STATUS_CONFLICT rows included.
 */

export type BoardView = 'week' | 'day' | 'month';

/**
 * The device's zone — what the board falls back to before (or without) the
 * organization's calendar, so a caller that passes no zone keeps the old
 * device-local behaviour exactly.
 */
const deviceZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * A board column's calendar date, `YYYY-MM-DD`. Columns are CALENDAR DAYS of
 * the organization, carried as Dates whose local components name the day; the
 * zone only enters where a day meets an instant (`boardWindow`, `inDay`,
 * `daySpan`).
 */
export function dayKey(day: Date): string {
  const m = String(day.getMonth() + 1).padStart(2, '0');
  const d = String(day.getDate()).padStart(2, '0');
  return `${day.getFullYear()}-${m}-${d}`;
}

/** Whether instant `iso` falls on calendar day `day` in `timeZone`. */
export function inDay(iso: string, day: Date, timeZone: string = deviceZone()): boolean {
  return localDateIn(timeZone, new Date(iso)) === dayKey(day);
}

/**
 * Which of `days` instant `iso` falls on in `timeZone`, or -1. Each item's
 * org-day is worked out ONCE and looked up — not tested against every column,
 * which made the 42-day Month grid cost items × 42 date formats per render.
 */
function dayColumns(days: readonly Date[], timeZone: string): (iso: string) => number {
  const col = new Map(days.map((d, i) => [dayKey(d), i]));
  return (iso) => col.get(localDateIn(timeZone, new Date(iso))) ?? -1;
}

/**
 * The canvas's day columns: the week containing `anchor` that starts on
 * `weekStartsOn` (0 = Sunday … 6 = Saturday — the organization's
 * `week_starts_on`, so the board's week is the week every analytics figure
 * counts), the single day, or — for `month` — the 6 × 7 grid around the
 * anchor's month starting on the same weekday (leading/trailing days belong to
 * the neighbouring months).
 *
 * DST-SAFE BY CONSTRUCTION: columns are built from the anchor's LOCAL calendar
 * components, never by adding `86_400_000` ms. Millisecond day-walking is wrong
 * by an hour twice a year (see `lib/timeFormat.ts`) — on the two DST weeks the
 * Sunday column landed at 23:00 Saturday or 01:00 Monday, so `sameLocalDay`
 * mis-bucketed shifts on the wrong day and `boardWindow` read a window an hour
 * off at the seam.
 */
export function boardDays(anchor: Date, view: BoardView, weekStartsOn = 1): Date[] {
  const base = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  if (view === 'day') return [base];
  if (view === 'month') {
    const lead = (new Date(base.getFullYear(), base.getMonth(), 1).getDay() - weekStartsOn + 7) % 7;
    return Array.from(
      { length: 42 },
      (_, i) => new Date(base.getFullYear(), base.getMonth(), 1 - lead + i),
    );
  }
  const back = (base.getDay() - weekStartsOn + 7) % 7;
  const first = new Date(base.getFullYear(), base.getMonth(), base.getDate() - back);
  return Array.from(
    { length: 7 },
    (_, i) => new Date(first.getFullYear(), first.getMonth(), first.getDate() + i),
  );
}

/**
 * The [from, to) read window covering the board's columns: the first instant
 * of the first day to the first instant of the day after the last, both in
 * `timeZone` (the organization's). Day boundaries come from Intl, never from
 * adding 86_400_000 ms — same DST reasoning as `boardDays`.
 */
export function boardWindow(
  days: readonly Date[],
  timeZone: string = deviceZone(),
): { from: string; to: string } {
  const first = days[0]!;
  const last = days[days.length - 1]!;
  const next = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1);
  return {
    from: services.startOfDayIn(dayKey(first), timeZone).toISOString(),
    to: services.startOfDayIn(dayKey(next), timeZone).toISOString(),
  };
}

/**
 * The chip's operational state — PLAN meeting ACTUAL, in the coverage
 * vocabulary the product already has:
 *
 *   `cancelled` — called off; drawn struck, never as a gap.
 *   `live`      — a session is OPEN against it right now.
 *   `covered`   — a session fulfilled it (any non-open session).
 *   `gap`       — it started and nothing was recorded (coverageGaps' rule).
 *   `upcoming`  — the future; not a gap, not an alarm.
 */
export type ChipState = 'cancelled' | 'live' | 'covered' | 'gap' | 'upcoming';

export function chipState(shift: SCH.ShiftWithCoverage, now: Date): ChipState {
  if (shift.status === 'cancelled') return 'cancelled';
  if (shift.session) return shift.session.status === 'open' ? 'live' : 'covered';
  return new Date(shift.startsAt).getTime() <= now.getTime() ? 'gap' : 'upcoming';
}

/**
 * One calendar state across both kinds. Shifts speak coverage (`ChipState`);
 * visits speak the canonical bucket:
 *
 *   `upcoming`     — a shift not yet started, a visit booked (or a hand-booked
 *                    visit still `scheduled`: no inference from the clock).
 *   `delivered`    — the canonical delivered bucket. Started (empty) and
 *                    Incomplete are sub-labels (`detail`), not states.
 *   `not_started`  — a past occurrence that never began and no encounter proves.
 *   `no_show`      — the EHR's no-show.
 *   `cancelled`    — cancelled (a lifecycle cancellation outranks vendor-absence).
 *   `unlisted`     — the EHR no longer lists it: an observation, NOT a cancellation.
 *   `unclassified` — a vendor status Phibian does not recognise.
 */
export type CalendarState =
  ChipState | 'delivered' | 'not_started' | 'no_show' | 'unlisted' | 'unclassified';

/** Lower-case so it reads inside a sentence and on the legend alike. */
export const CALENDAR_STATE_LABEL: Record<CalendarState, string> = {
  upcoming: 'scheduled',
  live: 'on the clock',
  covered: 'worked',
  gap: 'started, no session',
  delivered: 'delivered',
  not_started: 'not started',
  no_show: 'no-show',
  cancelled: 'cancelled',
  unlisted: 'no longer in the EHR',
  unclassified: 'status not recognised',
};

/**
 * A state in words for any surface (calendar, drawer, Visits, worker profile):
 * `unlisted` names the EHR when its connector can be named — "no longer in
 * Ritten" — else "the EHR". The connector's own display name, never a guess.
 */
export function calendarStateLabel(state: CalendarState, connectorName: string | null): string {
  return state === 'unlisted' && connectorName
    ? `no longer in ${connectorName}`
    : CALENDAR_STATE_LABEL[state];
}

/** The Pill variant each state is drawn with (drawer, worker profile). */
export const CALENDAR_STATE_TONE: Record<
  CalendarState,
  'good' | 'look' | 'unsure' | 'plain' | 'sky'
> = {
  upcoming: 'unsure',
  live: 'sky',
  covered: 'good',
  gap: 'look',
  delivered: 'good',
  not_started: 'look',
  no_show: 'look',
  cancelled: 'plain',
  unlisted: 'plain',
  unclassified: 'unsure',
};

/** Said when a blank past occurrence counts as delivered on its encounter's minutes. */
export const CONFLICT_DETAIL = 'EHR status blank — an encounter with minutes is recorded';

/** Why an imported occurrence is not clean, in words. Exhaustive by type. */
export const REASON_CODE_LABELS: Record<SVC.OccurrenceReasonCode, string> = {
  STAFF_UUID_NOT_SUPPLIED: 'The EHR named no staff member',
  STAFF_UUID_NOT_MAPPED: 'The EHR’s staff member is not matched to anyone here',
  HISTORICAL_IDENTITY_PENDING_RECONCILIATION: 'A former staff identity, still being matched',
  MULTIPLE_STAFF: 'A group visit with more than one staff member',
  CLIENT_UUID_NOT_SUPPLIED: 'The EHR named no client',
  CLIENT_UUID_NOT_MAPPED: 'The EHR’s client is not matched to anyone here',
  MULTIPLE_CLIENTS: 'A group visit with more than one client',
  PARTIES_NOT_RECORDED: 'Who took part was never recorded',
  UNKNOWN_EVENT_STATUS: 'The EHR status is not one Phibian recognises',
  CALENDAR_UNAVAILABLE: 'Not matched to the EHR calendar',
  EVENT_RECONSTRUCTED_FROM_EVIDENCE: 'Rebuilt from the EHR’s records, not read from its calendar',
};

/** A group visit is not a problem; every other reason code asks a person to look. */
export const GROUP_CODES: ReadonlySet<SVC.OccurrenceReasonCode> = new Set([
  'MULTIPLE_STAFF',
  'MULTIPLE_CLIENTS',
]);

/**
 * A visit's calendar state from the canonical bucket. A hand-booked visit has
 * no bucket (its delivery is its own service event) and maps through Phibian's
 * own status exactly as scheduled-work's `workStateOf` does — `scheduled` stays
 * scheduled whatever the clock says.
 */
export function visitState(
  v: Pick<
    SVC.ScheduledService,
    'deliveredBucket' | 'occurrenceState' | 'sourceStatusConflict' | 'status'
  >,
): { state: CalendarState; detail: string | null } {
  switch (v.deliveredBucket) {
    case 'delivered':
      return {
        state: 'delivered',
        detail: v.sourceStatusConflict
          ? CONFLICT_DETAIL
          : v.occurrenceState === 'started_empty' || v.occurrenceState === 'incomplete'
            ? services.OCCURRENCE_STATE_LABELS[v.occurrenceState]
            : null,
      };
    case 'booked':
      return { state: 'upcoming', detail: null };
    case 'not_started':
    case 'no_show':
    case 'cancelled':
      return { state: v.deliveredBucket, detail: null };
    case 'vendor_absent':
      return { state: 'unlisted', detail: null };
    case 'other_status':
      return { state: 'unclassified', detail: null };
    default:
      switch (v.status) {
        case 'scheduled':
        case 'confirmed':
          return { state: 'upcoming', detail: null };
        case 'completed':
          return { state: 'delivered', detail: null };
        case 'cancelled':
        case 'no_show':
          return { state: v.status, detail: null };
        default:
          return { state: 'unclassified', detail: null };
      }
  }
}

interface ItemBase {
  id: string;
  /** Every worker it sits under. Empty → the Unassigned row. */
  workerIds: string[];
  startsAt: string;
  /** A visit with no end draws from its start (see `daySpan`'s minimum width). */
  endsAt: string;
  clientId: string | null;
  programId: string | null;
  siteId: string | null;
  /** `ehr` when an import booked it, `phibian` when a person here did. */
  origin: 'ehr' | 'phibian';
  state: CalendarState;
  /** A delivered sub-label (Started (empty) / Incomplete) or the conflict note. */
  detail: string | null;
  /** A person should look: an unclean occurrence, nobody staffed, or a status conflict. */
  unresolved: boolean;
}

export type CalendarItem =
  | (ItemBase & { kind: 'shift'; shift: SCH.ShiftWithCoverage })
  | (ItemBase & { kind: 'visit'; visit: SVC.ScheduledService });

/** Both reads as one list of items — one per record, never a merged record. */
export function toCalendarItems(
  shifts: readonly SCH.ShiftWithCoverage[],
  visits: readonly SVC.ScheduledService[],
  now: Date,
): CalendarItem[] {
  const items: CalendarItem[] = shifts.map((s) => ({
    kind: 'shift',
    shift: s,
    id: s.id,
    workerIds: [s.workerId],
    startsAt: s.startsAt,
    endsAt: s.endsAt,
    clientId: null,
    programId: s.programId,
    siteId: s.locationId,
    origin: s.source === 'imported' ? 'ehr' : 'phibian',
    state: chipState(s, now),
    detail: null,
    unresolved: false,
  }));
  for (const v of visits) {
    const workerIds = v.workerIds ?? (v.workerId ? [v.workerId] : []);
    const { state, detail } = visitState(v);
    items.push({
      kind: 'visit',
      visit: v,
      id: v.id,
      workerIds,
      startsAt: v.startsAt,
      endsAt: v.endsAt ?? v.startsAt,
      clientId: v.clientId,
      programId: v.programId,
      siteId: null,
      origin: v.source === 'imported' ? 'ehr' : 'phibian',
      state,
      detail,
      unresolved:
        workerIds.length === 0 ||
        v.sourceStatusConflict === true ||
        v.reasonCodes.some((c) => !GROUP_CODES.has(c)),
    });
  }
  return items;
}

export interface BoardRow {
  /** Null is the Unassigned row: visits with no resolved staff. */
  workerId: string | null;
  /** One cell per board day, in column order. */
  cells: CalendarItem[][];
}

/**
 * Workers × days. Rows are the workers WITH items in the window, plus one
 * Unassigned row when some visit resolves to nobody (a shift cannot — its
 * worker is NOT NULL). Callers may append roster workers with nothing in the
 * window behind an explicit "show everyone" choice.
 */
export function boardRows(
  items: readonly CalendarItem[],
  days: readonly Date[],
  timeZone: string = deviceZone(),
): BoardRow[] {
  const byWorker = new Map<string | null, CalendarItem[][]>();
  const columnOf = dayColumns(days, timeZone);
  for (const item of items) {
    const col = columnOf(item.startsAt);
    if (col === -1) continue;
    for (const key of item.workerIds.length > 0 ? item.workerIds : [null]) {
      let cells = byWorker.get(key);
      if (!cells) {
        cells = days.map(() => []);
        byWorker.set(key, cells);
      }
      cells[col]!.push(item);
    }
  }
  for (const cells of byWorker.values()) {
    for (const cell of cells) cell.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  }
  return [...byWorker.entries()].map(([workerId, cells]) => ({ workerId, cells }));
}

export interface CalendarFilter {
  workerId?: string;
  clientId?: string;
  programId?: string;
  siteId?: string;
  origin?: 'ehr' | 'phibian';
  /** A state, or `unresolved` for the needs-a-look flag. */
  state?: CalendarState | 'unresolved';
}

/**
 * Client-side narrowing by the items' own columns — never a claim about reach.
 * A visit carries no site, so a site filter keeps shifts only (the screen says so).
 */
export function filterItems(items: readonly CalendarItem[], f: CalendarFilter): CalendarItem[] {
  return items.filter(
    (i) =>
      (!f.workerId || i.workerIds.includes(f.workerId)) &&
      (!f.clientId || i.clientId === f.clientId) &&
      (!f.programId || i.programId === f.programId) &&
      (!f.siteId || i.siteId === f.siteId) &&
      (!f.origin || i.origin === f.origin) &&
      (!f.state || (f.state === 'unresolved' ? i.unresolved : i.state === f.state)),
  );
}

/** How many items wear each state. Each item counts once, however many rows it sits in. */
export function tally(items: readonly CalendarItem[]): Partial<Record<CalendarState, number>> {
  const t: Partial<Record<CalendarState, number>> = {};
  for (const i of items) t[i.state] = (t[i.state] ?? 0) + 1;
  return t;
}

/** The Month view's cells: `tally` per organization day. */
export function tallyByDay(
  items: readonly CalendarItem[],
  days: readonly Date[],
  timeZone: string = deviceZone(),
): Partial<Record<CalendarState, number>>[] {
  const columnOf = dayColumns(days, timeZone);
  const cells = days.map((): CalendarItem[] => []);
  for (const i of items) cells[columnOf(i.startsAt)]?.push(i);
  return cells.map(tally);
}

/** The Day view's time axis, in whole hours. */
export const DAY_AXIS = { startHour: 6, endHour: 22 } as const;

/**
 * Where an item's bar sits on the Day axis, as fractional [start, end) hours
 * clipped to the axis. An item OFF the axis (before 6a, from 10p) is pinned to
 * the nearest edge as a quarter-hour bar and flagged `offAxis` — never dropped,
 * so the Day view holds every item the Month cell counted. The bar's own text
 * carries the real time.
 */
export function daySpan(
  item: Pick<SCH.Shift, 'startsAt' | 'endsAt'>,
  day: Date,
  timeZone: string = deviceZone(),
): { start: number; end: number; offAxis: boolean } {
  const dayStart = services.startOfDayIn(dayKey(day), timeZone);
  const h = (iso: string) => (new Date(iso).getTime() - dayStart.getTime()) / 3_600_000;
  // A quarter-hour minimum WIDTH, so a visit with no end time is still a bar
  // you can see and click. A drawing rule only — no duration is claimed.
  const MIN = 0.25;
  const s = h(item.startsAt);
  const e = Math.max(h(item.endsAt), s + MIN);
  const start = Math.min(Math.max(s, DAY_AXIS.startHour), DAY_AXIS.endHour - MIN);
  const end = Math.max(Math.min(e, DAY_AXIS.endHour), start + MIN);
  return { start, end, offAxis: e <= DAY_AXIS.startHour || s >= DAY_AXIS.endHour };
}
